import React, { useState } from 'react';
import { X, Send, Mail } from 'lucide-react';
import { timelineNotificationAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface TimelineNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeline: {
    id: number;
    title: string;
    description?: string;
    due_date?: string;
    status: string;
    publication_title: string;
    lead_author_email: string;
    lead_author_first_name: string;
    lead_author_last_name: string;
  };
}

const TimelineNotificationModal: React.FC<TimelineNotificationModalProps> = ({
  isOpen,
  onClose,
  timeline
}) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form when modal opens
  React.useEffect(() => {
    if (isOpen && timeline) {
      const defaultSubject = `Timeline Update: ${timeline.title}`;
      const defaultBody = `
        <h2>Timeline Update Notification</h2>
        <p>Dear ${timeline.lead_author_first_name} ${timeline.lead_author_last_name},</p>
        
        <p>This is a notification regarding the timeline item for your publication:</p>
        
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 15px 0;">
          <h3>Publication: ${timeline.publication_title}</h3>
          <h4>Timeline Item: ${timeline.title}</h4>
          ${timeline.description ? `<p><strong>Description:</strong> ${timeline.description}</p>` : ''}
          ${timeline.due_date ? `<p><strong>Due Date:</strong> ${new Date(timeline.due_date).toLocaleDateString()}</p>` : ''}
          <p><strong>Status:</strong> ${timeline.status.replace('_', ' ')}</p>
        </div>
        
        <p>Please review this timeline item and take any necessary actions.</p>
        
        <p>Best regards,<br>
        Publication Committee</p>
      `;
      
      setSubject(defaultSubject);
      setBody(defaultBody);
      setRecipientEmail(timeline.lead_author_email);
    }
  }, [isOpen, timeline]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || !recipientEmail.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      await timelineNotificationAPI.sendNotification(timeline.id, {
        subject: subject.trim(),
        body: body.trim(),
        recipientEmail: recipientEmail.trim()
      });
      
      toast.success('Timeline notification sent successfully!');
      onClose();
    } catch (error: any) {
      console.error('Error sending timeline notification:', error);
      toast.error(error.response?.data?.error || 'Failed to send notification');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Mail className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Send Timeline Notification</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Timeline Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Timeline Information</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Publication:</strong> {timeline.publication_title}</p>
              <p><strong>Timeline Item:</strong> {timeline.title}</p>
              {timeline.description && <p><strong>Description:</strong> {timeline.description}</p>}
              {timeline.due_date && <p><strong>Due Date:</strong> {new Date(timeline.due_date).toLocaleDateString()}</p>}
              <p><strong>Status:</strong> {timeline.status.replace('_', ' ')}</p>
            </div>
          </div>

          {/* Recipient Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient Email
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter recipient email"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter email subject"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Body (HTML)
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder="Enter email body (HTML format supported)"
            />
            <p className="text-xs text-gray-500 mt-1">
              You can use HTML tags for formatting. The email will be sent as HTML.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Send className="h-4 w-4" />
            )}
            <span>{isLoading ? 'Sending...' : 'Send Notification'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimelineNotificationModal;

